import React, { ChangeEvent } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Table from 'react-bootstrap/Table';
import imgDelete from '../../../../../assets/images/delete.svg';
import { FieldTooltipBadge } from './FieldTooltipBadge';
import { OfferPositionFormValues } from './offerFormLogic';

interface PositionsTableProps {
  positions: OfferPositionFormValues[]
  positionKeys: number[]
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  positionError: (index: number, field: 'episode_uuid' | 'node_name' | 'step_position') => string
  onAddPosition: () => void
  onRemovePosition: (index: number) => void
}

// Captions once as column headers instead of repeated per row -- three positions fit without
// scrolling, where the old one-block-per-row layout took a full screen for two or three. Add,
// edit and remove all still go through the same handlers useOfferFormFields already owns; only
// the markup changed, so buildOfferPayload's positions array is untouched.
export function PositionsTable(props: PositionsTableProps) {
  const {
    positions, positionKeys, onChange, positionError, onAddPosition, onRemovePosition,
  } = props;

  return (
    <>
      <Table size="sm" responsive>
        <thead>
          <tr>
            <th>
              <FieldTooltipBadge
                id="episode-uuid-tooltip"
                title="Episode identifier"
                content="Not the title or chapter number -- the episode's own ID/UUID, from its settings in the authoring tool."
              >
                Episode identifier
              </FieldTooltipBadge>
            </th>
            <th>
              <FieldTooltipBadge
                id="node-name-tooltip"
                title="Node name"
                content="Must match the authoring tool exactly, capitalisation and spacing included. Any mismatch means the offer silently never appears for players."
              >
                Node name
              </FieldTooltipBadge>
            </th>
            <th>
              <FieldTooltipBadge
                id="step-position-tooltip"
                title="Step number"
                content="Zero-based: 0 is the first step in this node, 1 the second, and so on."
              >
                Step number
              </FieldTooltipBadge>
            </th>
            <th aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {positions.map((position, index) => (
            <tr key={positionKeys[index]}>
              <td>
                <Form.Group className="mb-0">
                  <Form.Control
                    type="text"
                    size="sm"
                    name={`positions[${index}].episode_uuid`}
                    value={position.episode_uuid}
                    onChange={onChange}
                    isInvalid={!!positionError(index, 'episode_uuid')}
                  />
                  <Form.Control.Feedback type="invalid">
                    {positionError(index, 'episode_uuid')}
                  </Form.Control.Feedback>
                </Form.Group>
              </td>
              <td>
                <Form.Group className="mb-0">
                  <Form.Control
                    type="text"
                    size="sm"
                    name={`positions[${index}].node_name`}
                    value={position.node_name}
                    onChange={onChange}
                    isInvalid={!!positionError(index, 'node_name')}
                  />
                  <Form.Control.Feedback type="invalid">
                    {positionError(index, 'node_name')}
                  </Form.Control.Feedback>
                </Form.Group>
              </td>
              <td>
                <Form.Group className="mb-0">
                  <Form.Control
                    type="number"
                    size="sm"
                    name={`positions[${index}].step_position`}
                    value={position.step_position}
                    onChange={onChange}
                    isInvalid={!!positionError(index, 'step_position')}
                    min={0}
                    step={1}
                  />
                  <Form.Control.Feedback type="invalid">
                    {positionError(index, 'step_position')}
                  </Form.Control.Feedback>
                </Form.Group>
              </td>
              <td className="align-middle">
                {positions.length > 1 && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => onRemovePosition(index)}
                    aria-label={`Remove position ${index + 1}`}
                  >
                    <img src={imgDelete} className="btnImg" alt="Remove" />
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Button type="button" variant="outline-primary" onClick={onAddPosition}>
        Add another position
      </Button>
    </>
  );
}
